import type { ReactNode } from 'react';

import { Text } from '@spotify-internal/encore-web';

import { ToolGroupEntry } from '../../extensions/tool-extension';
import { useToolExtensions } from '../../extensions/tool-extension';
import { ChatInformationBox } from '../ChatInformationBox';
import { ElapsedTimer } from '../ElapsedTimer';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { sharedToolStyles } from './sharedStyles';

import styles from './DefaultToolCallGroup.module.css';

const ToolEntryLabel = ({
  entry,
  iconSize = 14,
}: {
  entry: ToolGroupEntry;
  iconSize?: number;
}) => {
  const toolExtensions = useToolExtensions();
  const ext = toolExtensions.find(e => e.predicate(entry.tool));

  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  let labelContent: ReactNode;
  if (ext?.renderLabel) {
    labelContent = ext.renderLabel(entry);
  } else {
    const label = ext?.formatLabel
      ? ext.formatLabel(entry)
      : formatDefaultToolLabel(entry);
    labelContent = (
      <Text variant="bodySmall" className={styles.text}>
        {label.text}
        {label.detail && (
          <span className={styles.text}>{` ${label.detail}`}</span>
        )}
      </Text>
    );
  }

  const icon = (
    <IconComponent size={iconSize} className={sharedToolStyles.icon} />
  );
  if (entry.startedAt === undefined) {
    return (
      <>
        {icon}
        {labelContent}
      </>
    );
  }

  return (
    <span className={styles.label}>
      {icon}
      {labelContent}
      <ElapsedTimer startedAt={entry.startedAt} />
    </span>
  );
};

export const DefaultToolCallGroup = ({
  entries,
}: {
  entries: ToolGroupEntry[];
}) => {
  const toolExtensions = useToolExtensions();

  if (entries.length === 1) {
    const entry = entries[0];
    const ext = toolExtensions.find(e => e.predicate(entry.tool));

    if (ext?.display === 'prominent' && ext.renderTool) {
      const renderedTool = ext.renderTool(entry);
      if (renderedTool) {
        return (
          <>
            {renderedTool}
            {entry.startedAt !== undefined && (
              <ElapsedTimer startedAt={entry.startedAt} />
            )}
          </>
        );
      }
      return (
        <div className={styles.listItem}>
          <ToolEntryLabel entry={entry} iconSize={12} />
        </div>
      );
    }

    return <ChatInformationBox title={<ToolEntryLabel entry={entry} />} />;
  }

  return (
    <div className={styles.list}>
      {entries.map((entry, i) => {
        const ext = toolExtensions.find(e => e.predicate(entry.tool));

        if (ext?.display === 'prominent' && ext.renderTool) {
          const renderedTool = ext.renderTool(entry);
          if (!renderedTool) {
            return (
              <div key={i} className={styles.listItem}>
                <ToolEntryLabel entry={entry} iconSize={12} />
              </div>
            );
          }

          return (
            <div key={i}>
              {renderedTool}
              {entry.startedAt !== undefined && (
                <ElapsedTimer startedAt={entry.startedAt} />
              )}
            </div>
          );
        }

        return (
          <div key={i} className={styles.listItem}>
            <ToolEntryLabel entry={entry} iconSize={12} />
          </div>
        );
      })}
    </div>
  );
};
